// Top-header notification bell: unread badge + dropdown panel of recent
// notifications, live-updated over the realtime socket. Inline-styled with the
// EM Hub design tokens to match the rest of the app.
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Bell,
  Plane,
  Clock,
  AlarmClock,
  CalendarClock,
  CheckCheck,
  Inbox,
  Check,
  X,
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { T } from "../../components/em/tokens";
import { API_URL } from "../../api/api";
import {
  notificationsApi,
  roleBase,
  resolveNotifLink,
  notifMeta,
} from "./notificationsApi";
import useNotificationSocket from "./useNotificationSocket";

// Inline approvals: admins/HR can act on a leave request straight from the
// dropdown when the notification carries meta.leave_id.
const canApproveInline = () =>
  ["admin", "hr"].includes(
    (localStorage.getItem("role") || "").toLowerCase().trim(),
  );

const isActionable = (n) =>
  n?.type === "leave.requested" &&
  n?.meta?.leave_id &&
  !n.read &&
  canApproveInline();

// Map notifMeta().icon → lucide component.
const ICONS = {
  plane: Plane,
  clock: Clock,
  "alarm-clock": AlarmClock,
  "calendar-clock": CalendarClock,
};
const iconFor = (type) => ICONS[notifMeta(type).icon] || Bell;

// Compact relative-time helper ("just now", "5m ago", "3h ago", "2d ago").
export const relTime = (ts) => {
  if (!ts) return "";
  const then = new Date(ts).getTime();
  if (Number.isNaN(then)) return "";
  const s = Math.max(0, Math.floor((Date.now() - then) / 1000));
  if (s < 45) return "just now";
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d ago`;
  const w = Math.floor(d / 7);
  if (w < 5) return `${w}w ago`;
  return new Date(ts).toLocaleDateString();
};

function ActionBtn({ icon: Icon, label, tone, busy, onClick }) {
  return (
    <button
      type="button"
      disabled={busy}
      onClick={onClick}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 5,
        height: 26,
        margin: 0,
        padding: "0 10px",
        lineHeight: 1,
        borderRadius: T.pill,
        cursor: busy ? "default" : "pointer",
        border: `1px solid ${tone === "danger" ? T.danger : T.success}`,
        background: tone === "danger" ? T.danger50 : T.success50,
        color: tone === "danger" ? T.danger : T.success,
        font: `600 12px/1 ${T.font}`,
        opacity: busy ? 0.6 : 1,
      }}
    >
      <Icon size={13} /> {label}
    </button>
  );
}

// A single notification row (shared visual language with the full page).
// Root is a div (not a button) so the inline action buttons can nest legally.
function NotifRow({ n, onClick, onAction, busyAction }) {
  const Icon = iconFor(n.type);
  const tone = notifMeta(n.type).tone;
  const [hover, setHover] = useState(false);
  const actionable = isActionable(n) && !n.actioned;
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onClick(n)}
      onKeyDown={(e) => {
        if (e.key === "Enter") onClick(n);
      }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: "flex",
        gap: 12,
        alignItems: "flex-start",
        width: "100%",
        textAlign: "left",
        cursor: "pointer",
        border: 0,
        borderBottom: `1px solid ${T.divider}`,
        padding: "12px 14px",
        background: n.read
          ? hover
            ? T.bgMuted
            : "transparent"
          : hover
            ? "rgba(245,75,1,0.12)"
            : T.orangeSoft,
        transition: "background .12s",
      }}
    >
      <div
        style={{
          width: 34,
          height: 34,
          borderRadius: T.pill,
          flexShrink: 0,
          background: `${tone}1a`,
          color: tone,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Icon size={16} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <span
            style={{
              flex: 1,
              minWidth: 0,
              font: `600 13px/1.3 ${T.font}`,
              color: T.fg1,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {n.title}
          </span>
          {!n.read && (
            <span
              style={{
                width: 7,
                height: 7,
                borderRadius: T.pill,
                background: T.orange,
                flexShrink: 0,
              }}
            />
          )}
        </div>
        {n.body && (
          <div
            style={{
              marginTop: 2,
              font: `400 12px/1.45 ${T.font}`,
              color: T.fg5,
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
          >
            {n.body}
          </div>
        )}
        {actionable && (
          <div
            style={{ display: "flex", gap: 8, marginTop: 8 }}
            onClick={(e) => e.stopPropagation()}
          >
            <ActionBtn
              icon={Check}
              label="Approve"
              tone="success"
              busy={busyAction === n.id}
              onClick={() => onAction(n, "approved")}
            />
            <ActionBtn
              icon={X}
              label="Reject"
              tone="danger"
              busy={busyAction === n.id}
              onClick={() => onAction(n, "rejected")}
            />
          </div>
        )}
        {n.actioned && (
          <div
            style={{
              marginTop: 8,
              font: `600 12px/1 ${T.font}`,
              color: n.actioned === "approved" ? T.success : T.danger,
            }}
          >
            {n.actioned === "approved" ? "✓ Approved" : "✕ Rejected"}
          </div>
        )}
        <div
          style={{ marginTop: 4, font: `400 11px/1 ${T.font}`, color: T.fg7 }}
        >
          {relTime(n.created_at)}
        </div>
      </div>
    </div>
  );
}

export default function NotificationBell() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const wrapRef = useRef(null);
  const openRef = useRef(open);
  openRef.current = open;

  // Initial unread badge.
  useEffect(() => {
    let alive = true;
    notificationsApi
      .unreadCount()
      .then((u) => {
        if (alive) setUnread(u || 0);
      })
      .catch(() => { });
    return () => {
      alive = false;
    };
  }, []);

  // Realtime pushes.
  useNotificationSocket((n) => {
    setUnread((u) => u + 1);
    if (openRef.current) setItems((list) => [n, ...list]);
    try {
      if (n?.title) toast(n.title, { description: n.body || undefined });
    } catch {
      /* noop */
    }
  });

  // Outside-click + Escape to close.
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target))
        setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const loadRecent = () => {
    setLoading(true);
    notificationsApi
      .list({ limit: 10 })
      .then((r) => {
        setItems(r.notifications || []);
        if (typeof r.unread === "number") setUnread(r.unread);
      })
      .catch(() => { })
      .finally(() => setLoading(false));
  };

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next) loadRecent();
  };

  const handleMarkAll = () => {
    setUnread(0);
    setItems((list) => list.map((n) => ({ ...n, read: true })));
    notificationsApi
      .markAllRead()
      .then((r) => {
        if (r && typeof r.unread === "number") setUnread(r.unread);
      })
      .catch(() => { });
  };

  const handleRow = (n) => {
    if (!n.read) {
      setItems((list) =>
        list.map((x) => (x.id === n.id ? { ...x, read: true } : x)),
      );
      setUnread((u) => Math.max(0, u - 1));
      notificationsApi
        .markRead(n.id)
        .then((r) => {
          if (r && typeof r.unread === "number") setUnread(r.unread);
        })
        .catch(() => { });
    }
    setOpen(false);
    const to = resolveNotifLink(n.link);
    if (to) navigate(to);
  };

  // Inline Approve / Reject on a leave.requested notification. Acts on the
  // leave, then marks this notification read and shows the outcome in place.
  const [busyAction, setBusyAction] = useState(null);
  const handleAction = async (n, status) => {
    if (busyAction) return;
    setBusyAction(n.id);
    try {
      const token = localStorage.getItem("token");
      await axios.put(
        `${API_URL}/api/leaves/status/${n.meta.leave_id}`,
        { status, comment: null },
        token ? { headers: { Authorization: `Bearer ${token}` } } : {},
      );
      setItems((list) =>
        list.map((x) =>
          x.id === n.id ? { ...x, read: true, actioned: status } : x,
        ),
      );
      setUnread((u) => Math.max(0, u - 1));
      notificationsApi
        .markRead(n.id)
        .then((r) => {
          if (r && typeof r.unread === "number") setUnread(r.unread);
        })
        .catch(() => { });
      toast.success(
        status === "approved" ? "Leave approved" : "Leave rejected",
      );
    } catch (err) {
      toast.error(err?.response?.data?.error || "Could not update the leave.");
    } finally {
      setBusyAction(null);
    }
  };

  const badge = unread > 9 ? "9+" : String(unread);

  return (
    <div
      ref={wrapRef}
      style={{
        position: "relative",
        display: "flex",
        width: 40,
        height: 40,
      }}
    >
      <button
        type="button"
        aria-label="Notifications"
        onClick={toggle}
        style={{
          position: "relative",
          width: 40,
          height: 40,
          borderRadius: T.pill,
          padding: 0,
          border: 0,
          margin: 0,
          background: open ? T.bgMuted : "transparent",
          cursor: "pointer",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          color: T.fg3,
        }}
      >
        <Bell size={20} />
        {unread > 0 && (
          <span
            style={{
              position: "absolute",
              top: 4,
              right: 4,
              minWidth: 16,
              height: 16,
              padding: "0 4px",
              borderRadius: T.pill,
              background: T.orange,
              color: "#fff",
              border: "2px solid #fff",
              font: `700 9px/1 ${T.font}`,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {badge}
          </span>
        )}
      </button>

      {open && (
        <div
          style={{
            position: "absolute",
            top: "calc(100% + 8px)",
            right: 0,
            width: 360,
            maxWidth: "92vw",
            background: T.bgCard,
            border: `1px solid ${T.border}`,
            borderRadius: T.rLg,
            boxShadow: T.shadowMd,
            zIndex: 1200,
            overflow: "hidden",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "12px 14px",
              borderBottom: `1px solid ${T.divider}`,
            }}
          >
            <span style={{ font: `700 14px/1 ${T.font}`, color: T.fg1 }}>
              Notifications
            </span>
            <button
              type="button"
              onClick={handleMarkAll}
              disabled={unread === 0}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 5,
                border: 0,
                background: "transparent",
                cursor: unread === 0 ? "default" : "pointer",
                color: unread === 0 ? T.fg7 : T.orange,
                font: `600 12px/1 ${T.font}`,
                padding: 4,
              }}
            >
              <CheckCheck size={14} /> Mark all read
            </button>
          </div>

          <div style={{ maxHeight: 380, overflowY: "auto" }}>
            {loading && items.length === 0 ? (
              <div
                style={{
                  padding: "40px 14px",
                  textAlign: "center",
                  font: `400 13px ${T.font}`,
                  color: T.fg5,
                }}
              >
                Loading…
              </div>
            ) : items.length === 0 ? (
              <div style={{ padding: "44px 24px", textAlign: "center" }}>
                <div
                  style={{
                    width: 52,
                    height: 52,
                    borderRadius: T.pill,
                    background: T.bgMuted,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    margin: "0 auto 12px",
                  }}
                >
                  <Inbox size={24} color={T.fg7} />
                </div>
                <div style={{ font: `600 14px ${T.font}`, color: T.fg3 }}>
                  You're all caught up
                </div>
              </div>
            ) : (
              items.map((n) => (
                <NotifRow
                  key={n.id}
                  n={n}
                  onClick={handleRow}
                  onAction={handleAction}
                  busyAction={busyAction}
                />
              ))
            )}
          </div>

          <button
            type="button"
            onClick={() => {
              setOpen(false);
              navigate(`${roleBase()}/notifications`);
            }}
            style={{
              width: "100%",
              border: 0,
              borderTop: `1px solid ${T.divider}`,
              background: T.bgHead,
              padding: "11px 14px",
              cursor: "pointer",
              font: `600 13px/1 ${T.font}`,
              color: T.orange,
            }}
          >
            View all notifications
          </button>
        </div>
      )}
    </div>
  );
}
